// src/config/gameProfiles/designThinkingProfile.ts
// -------------------------------------------------------
// Design Thinking Role-Scenario game profile.
// Board is SERVER-driven memory board (via digiboard.php). Avatar never outputs board JSON.
// -------------------------------------------------------

import type { GameProfile, GameState, GameEvent } from './GameProfile';
import { determineAttemptOutcome } from '../../engine/determineAttemptOutcome';
// ── Phase definitions ─────────────────────────────────────

export const DT_PHASES = ['SCENE', 'EXPLORE', 'ATTEMPT', 'CONSEQUENCE', 'REFLECT'] as const;
export type DTPhase = (typeof DT_PHASES)[number];

// ── Assumption / clue types for custom state ─────────────

export interface DTAssumption {
  text: string;
  status: 'active' | 'confirmed' | 'discarded';
  note?: string;
}

export interface DTClue {
  text: string;
  unlocked: boolean;
  source: string; // e.g. "Noor vertelde..." or "Je zag dat..."
}

export interface DTCustomState {
  assumptions: DTAssumption[];
  clues: DTClue[];
  attemptCount: number;
  stuckCount: number; // consecutive turns without investigation
  lastAction?: string;
  missionComplete: boolean;
}

function getCustom(state: GameState): DTCustomState {
  const base = createDefaultCustom();

  const cur = (state.custom && typeof state.custom === "object")
    ? (state.custom as any)
    : {};

  const normalized: DTCustomState = {
    assumptions: Array.isArray(cur.assumptions) ? cur.assumptions : base.assumptions,
    clues: Array.isArray(cur.clues) ? cur.clues : base.clues,
    attemptCount: Number.isFinite(cur.attemptCount) ? cur.attemptCount : base.attemptCount,
    stuckCount: Number.isFinite(cur.stuckCount) ? cur.stuckCount : base.stuckCount,
    lastAction: typeof cur.lastAction === "string" ? cur.lastAction : undefined,
    missionComplete: typeof cur.missionComplete === "boolean" ? cur.missionComplete : base.missionComplete,
  };

  // IMPORTANT: write it back so the rest of the app never sees a partial custom object
  state.custom = normalized as any;
  return normalized;
}

function createDefaultCustom(): DTCustomState {
  return {
    assumptions: [],
    clues: [],
    attemptCount: 0,
    stuckCount: 0,
    missionComplete: false,
  };
}

function ensureCustom(state: GameState): DTCustomState {
  if (!state.custom) state.custom = createDefaultCustom() as any;
  return getCustom(state);
}

// ── Ready tokens ──────────────────────────────────────────

const READY_TOKENS = ['ja', 'klaar', 'ready', 'ok', 'oke', 'oké', 'start', 'go', 'help', 'yes'] as const;

// ── Action-like keywords ──────────────────────────────────

const ACTION_KEYWORDS = [
  'kopen', 'plakken', 'tape', 'vastmaken', 'repareren', 'nieuwe kooi',
  'slot', 'dicht', 'vergrendel', 'bouw', 'maak', 'probeer', 'doe',
  'verander', 'vervang', 'fix', 'buy', 'build', 'try',
  'ik wil', 'laten we', 'we kunnen', 'ik ga',
] as const;

const QUESTION_KEYWORDS = [
  'waarom', 'hoe', 'wat', 'wanneer', 'waar', 'wie', 'welk',
  'kun je', 'is het', 'klopt het', 'weet je', 'vertel',
  'why', 'how', 'what', 'when', 'where', 'who',
  'kan ik', 'mag ik', 'laat zien', 'bekijk', 'onderzoek', 'check',
  '?',
] as const;

// ── Profile ──────────────────────────────────────────────

export const designThinkingProfile: GameProfile = {
  id: 'design-thinking',
  displayName: 'Design Thinking Missie',

  // ── Phases ──────────────────────────────────────────

  phases: DT_PHASES,
  initialPhase: 'SCENE',

  /**
   * Transition map:
   *   SCENE       --user_response-->  EXPLORE
   *   EXPLORE     --action_chosen-->  ATTEMPT
   *   EXPLORE     --ask_question-->   null (stay, loop)
   *   ATTEMPT     --consequence-->    CONSEQUENCE
   *   CONSEQUENCE --partial-->        EXPLORE (back to investigate)
   *   CONSEQUENCE --success-->        REFLECT
   *   REFLECT     --advance-->        null (game ends)
   */
  transition(state: GameState, event: GameEvent): string | null {
    const { phase } = state;
    const { type } = event;

    switch (phase) {
      case 'SCENE':
        if (type === 'ready' || type === 'user_response') return 'EXPLORE';
        return null;

      case 'EXPLORE':
        if (type === 'action_chosen') return 'ATTEMPT';
        if (type === 'ask_question') return null; // stay
        if (type === 'user_response') return null; // stay
        return null;

      case 'ATTEMPT':
        // After the avatar describes the attempt, it transitions to consequence
        if (type === 'consequence') return 'CONSEQUENCE';
        if (type === 'user_response') return null; // avatar hasn't responded yet
        return null;

      case 'CONSEQUENCE':
        if (type === 'success') return 'REFLECT';
        if (type === 'partial' || type === 'failure') return 'EXPLORE'; // back
        if (type === 'user_response') return null; // kid is reacting
        return null;

      case 'REFLECT':
        if (type === 'advance' || type === 'mission_complete') return null; // terminal
        return null;

      default:
        return null;
    }
  },

  // ── Board ──────────────────────────────────────────

boardSource: 'inferred',

  layoutByPhase: {
    SCENE: 'scenario-intro',
    EXPLORE: 'investigation',
    ATTEMPT: 'attempt',
    CONSEQUENCE: 'consequence',
    REFLECT: 'reflection',
  },

  blockTypes: [
    'hero', 'callout', 'chiprow',          // shared with KCV
    'assumption', 'clue', 'consequence',   // DT-specific
    'reflection', 'nextcheck',            // DT-specific
  ] as const,

  // ── Prompt injection ───────────────────────────────

  buildControlPrompt(state: GameState, _userText: string): string {
    const custom = getCustom(state);
    const { phase } = state;

    const base = `GAME ENGINE CONTEXT:
Fase: ${phase}
Beurt in fase: ${state.turnInPhase}
Totaal beurten: ${state.totalTurns}
Aannames tot nu toe: ${custom.assumptions.length > 0 ? custom.assumptions.map(a => `[${a.status}] ${a.text}`).join('; ') : 'geen'}
Ontdekte aanwijzingen: ${custom.clues.filter(c => c.unlocked).length}/${custom.clues.length}
Pogingen: ${custom.attemptCount}
Vast-telling: ${custom.stuckCount} beurten zonder onderzoek`;

    const phaseInstructions: Record<string, string> = {
      SCENE: `FASE-INSTRUCTIE (SCENE):
Presenteer de scene precies zoals beschreven in de missie.
Eindig met "Kun je me helpen?"
Wacht op de leerling. Geef GEEN tips of uitleg.
Maximaal 1 scene-beat per beurt.`,

EXPLORE: `FASE-INSTRUCTIE (EXPLORE):
De leerling onderzoekt het probleem.

BELANGRIJK:
- Jij (Bella) beschrijft observaties.
- De leerling hoeft niet fysiek aanwezig te zijn.
- Stel GEEN vragen als: "Heb je gezien..." of "Heb je gemerkt..."
- Geef zelf 1 concreet observatiesignaal wanneer dat logisch is.

Als de leerling de omgeving noemt (bijv. drukte, kinderen):
- Geef een observatiehint zoals:
  "Ik merk dat hij onrustiger is wanneer het druk is in de klas."
  of
  "Op rustige momenten lijkt hij veel kalmer."

Daarna stel je een denk-vraag zoals:
  "Wat zou dat verschil kunnen betekenen?"

Geef maximaal 1 observatie per beurt.
Geen uitleg. Geen mini-les.
Blijf in het mysterie.`,

ATTEMPT: (() => {
  const outcome = determineAttemptOutcome(
    custom.lastAction,
    custom.clues.filter(c => c.unlocked).length
  );

  let fixedFacts = "";

  if (outcome.success) {
    fixedFacts = `
FEITEN (ONVERANDERBAAR):
- Nibbles ontsnapt niet meer.
- Hij is zichtbaar rustiger.
- Zijn gedrag is duidelijk verbeterd.
`;
  } else if (outcome.symptomResolved && outcome.newSignal === "restlessness") {
    fixedFacts = `
FEITEN (ONVERANDERBAAR):
- Nibbles ontsnapt niet meer.
- De nieuwe kooi werkt technisch perfect.
- Na een paar dagen merken we nieuw gedrag: hij is onrustig of knaagt ergens anders.
`;
  } else {
    fixedFacts = `
FEITEN (ONVERANDERBAAR):
- De poging werkte niet zoals verwacht.
`;
  }

  return `
FASE-INSTRUCTIE (ATTEMPT):

De leerling probeerde: "${custom.lastAction}"

${fixedFacts}

JE MAG DEZE FEITEN NIET VERANDEREN.
JE MAG HET ONTSNAPPEN NIET OPNIEUW INTRODUCEREN.

Beschrijf deze feiten levendig en natuurlijk.
Begin met: "Een paar dagen later..."
Geef minimaal 4 zinnen.
Eindig met precies 1 observatievraag.
`;
})(),

CONSEQUENCE: `FASE-INSTRUCTIE (CONSEQUENCE):
Beschrijf wat er gebeurde na de poging.

Als de poging een symptoom oploste:
- Bevestig dat het symptoom verdwenen is.
- Maak duidelijk dat een NIEUW signaal zichtbaar wordt.
- Verschuif de centrale vraag naar dit nieuwe patroon.

Zeg NIET dat het oorspronkelijke probleem terugkomt.
Zeg NIET dat de kooi weer faalt.

Laat zien dat we het verkeerde probleem probeerden te fixen.

Eindig met een observatievraag die de leerling richting gedrag/omgeving stuurt.`,

      REFLECT: `FASE-INSTRUCTIE (REFLECT):
De missie is opgelost. Geef de verplichte afsluiting:
1) Wat bleek de echte oorzaak/oorzaken (1 zin)
2) Vraag: "Welke aanname maakte jij in het begin?"
3) Feedback: 1 ding goed gedaan, 1 ding volgende keer eerder doen
4) Zeg: "Einde missie."`,
    };

    return `${base}\n\n${phaseInstructions[phase] || ''}`;
  },

  buildBoardInstruction(_state: GameState): string {
    // DT uses server-side "memory board" generation (digiboard.php).
    // The avatar must NOT output board JSON.
    return '';
  },


  // ── Ready gate ─────────────────────────────────────

  readyGateEnabled: true,
  readyAcceptTokens: READY_TOKENS,
  readyPrompt: 'Hoi! Ik ben Bella, je Design Thinking gids. We gaan samen een mysterie oplossen. Zeg "ja" als je er klaar voor bent!',

  // ── Detection ──────────────────────────────────────

  detectKeywords: ['design thinking', 'ontwerpdenken', 'hamster', 'nibbles', 'noor'] as const,

  // ── Gatekeeper ─────────────────────────────────────

  // DT doesn't need answer-leak prevention (there's no single right answer)
  noAnswerPhases: [] as const,
  allowedBlockTypesInNoAnswerPhases: [] as const,
  forceQuestionMeta: false,

  // ── Event extraction ───────────────────────────────

  classifyUserInput(userText: string, state: GameState): GameEvent {
    const t = (userText || '').trim().toLowerCase();
    const { phase } = state;

    // In SCENE: any response = ready to explore
    if (phase === 'SCENE') {
      if ((READY_TOKENS as readonly string[]).includes(t)) {
        return { type: 'ready' };
      }
      return { type: 'user_response' };
    }

    // In EXPLORE: classify as question vs action
    if (phase === 'EXPLORE') {
      const isQuestion = (QUESTION_KEYWORDS as readonly string[]).some(kw => t.includes(kw));
      const isAction = (ACTION_KEYWORDS as readonly string[]).some(kw => t.includes(kw));

     if (isAction && !isQuestion) {
  const custom = getCustom(state);

  custom.lastAction = userText;
  custom.attemptCount++;

  // 🔒 NEW LOGIC:
  // If they attempt without investigation first,
  // mark this attempt as "premature"
  const premature = custom.clues.filter(c => c.unlocked).length === 0;

  custom.stuckCount = 0;
  Object.assign(state.custom, custom);

  return {
    type: 'action_chosen',
    payload: {
      action: userText,
      premature,
    }
  };
}

      if (isQuestion) {
        const custom = getCustom(state);
        custom.stuckCount = 0;
        Object.assign(state.custom, custom);
        return { type: 'ask_question', payload: { question: userText } };
      }

      // Neither clear question nor action — count as generic response, may indicate stuck
      const custom = getCustom(state);
      custom.stuckCount++;
      Object.assign(state.custom, custom);
      return { type: 'user_response', payload: { text: userText } };
    }

    // In CONSEQUENCE: user reacting
    return { type: 'user_response', payload: { text: userText } };
  },

  classifyAssistantResponse(
    reply: string,
    script: Record<string, unknown> | null,
    state: GameState
  ): GameEvent {
    const { phase } = state;
    const t = reply.toLowerCase();

// DT board is generated separately (memory board), so script.board is ignored.
void script;

    // Phase-specific classification
    if (phase === 'ATTEMPT') {
      // Avatar described what happened -> consequence
      return { type: 'consequence' };
    }

if (phase === 'CONSEQUENCE') {
  const custom = getCustom(state);

  const outcome = determineAttemptOutcome(
    custom.lastAction,
    custom.clues.filter(c => c.unlocked).length
  );

  if (outcome.success) {
    return { type: 'success' };
  }

  return { type: 'partial' };
}

    if (phase === 'REFLECT') {
      if (t.includes('einde missie')) {
        const custom = getCustom(state);
        custom.missionComplete = true;
        Object.assign(state.custom, custom);
        return { type: 'mission_complete' };
      }
    }

    return { type: 'assistant_spoke' };
  },
};
